import { useState, useCallback, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { AuthResponse } from '../types/auth';
import { Search } from '../types/search';
import { Context } from '../services/context';
import { IAccessList } from '../Components/EmployeeInfo';
import { INewAccess } from '../Components/AccessBlock/Item';
import {
  IOrganizationResponse,
  IOrganizationSettings,
  IStaffSettings,
  IStuffResponse,
  ISubdivisionResponse,
  ISubdivisionSettings,
} from '../types/configuration';
import { ILoginData, ResponseLoginData } from '../types/loginData';
import { AccessTree } from '../types/accessTree';
import { Subdivision } from '../types/subdivision';

type ServerResponse<T> = {
  data: T[];
};

type EntityResponse<T> = {
  data: T;
};
type Str = {
  str: URLSearchParams;
};

type UseEntityList<T, K> = {
  entityList: T[];
  getEntityList(search?: URLSearchParams): Promise<void>;
  getLogsList(search?: URLSearchParams): Promise<void>;
  initLoading: boolean;
  entity: T | null;
  loading: boolean;
  getEntity(id: number, search?: URLSearchParams): Promise<void>;
  getTree(id: number, page?: number, limit?: number): Promise<void>;
  getTreeWithIndeterminate(id: number): Promise<void>;
  getSearchStaffTree(id: number): Promise<void>;
  getSearchSubTree(id: number): Promise<void>;
  getSearchOrgTree(id: number): Promise<void>;
  search(str: URLSearchParams): Promise<void>;
  importer(id?: number): Promise<void>;
  changeEntity(id: number, type?: number, value?: boolean): Promise<void>;
  deleteEntity(id: number): Promise<void>;
  blockUser(id: number, method?: 'Delete' | 'Block'): Promise<void>;
  unblockUser(id: number): Promise<void>;
  createEntity(entityCreate: K): Promise<T>;
  logOut(): Promise<T>;
  login(entityCreate: K): Promise<T>;
  auth(entityCreate: K): Promise<T>;
  clearEntityList(): void;
  clearSearchResult(): void;
  getLoginData(id: number): Promise<ILoginData>;
  changeStatusStaff(id: number, status: number): Promise<any>;
  updateEntity(entityUpdate: T): Promise<T>;
  fullUpdateEntity(entityUpdate: any): Promise<any>;
  searchResult: Search;
  accessList: IAccessList;
  accessTree: AccessTree;
  changeAccess(id: number, newAccess: INewAccess): Promise<void>;
  changeAnalyticsAccess(id: number, newAccess: boolean): Promise<void>;
  sendEmail(email: string): Promise<any>;
  restorePassword(entity: T): Promise<void>;
  changePasswordAdmin(newPassword: string, id: number): Promise<void>;
  changePasswordUser(oldPassword: string, newPassword: string): Promise<void>;
  setHistory(array: T[]): Promise<void>;
  changeParent(
    newParentId: number,
    currentId: number,
    isStuff: boolean,
  ): Promise<void>;
  getAllSubdivisions(): Promise<Subdivision[]>;
  getAccessEmployee(idEmployee: number): Promise<void>;
  getConfigurationStuff(): Promise<IStaffSettings>;
  getConfigurationSubdivision(): Promise<ISubdivisionSettings>;
  getConfigurationOrganization(): Promise<IOrganizationSettings>;
  changeConfigurationStuff(staffSettings: IStaffSettings): Promise<void>;
  changeConfigurationSubdivision(
    subdivisionSettings: ISubdivisionSettings,
  ): Promise<void>;
  changeConfigurationOrganization(
    organizationSettings: IOrganizationSettings,
  ): Promise<void>;
};

type Messages<T, K> = Partial<Record<keyof UseEntityList<T, K>, string>>;

export const createEntityHook = <T, K>(
  entityKey: keyof Omit<typeof api, 'upload'>,
  messages?: Messages<T, K>,
) => (): UseEntityList<T, K> => {
  const [initLoading, setInitLoading] = useState(true);
  const [entityList, setEntityList] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<T | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [searchResult, setSearchResult] = useState<Search & Str>({
    data: {
      organizations: { organizations: [], totalCount: 1, totalPages: 1 },
      staffs: { staffs: [], totalCount: 1, totalPages: 1 },
      subdivisions: { subdivisions: [], totalCount: 1, totalPages: 1 },
    },
    str: new URLSearchParams(''),
  });
  const [accessList, setAccessList] = useState<IAccessList>({
    organizations: [],
    subdivisions: [],
  });
  const [accessTree, setAccessTree] = useState<AccessTree>({
    checkboxType: 0,
    children: [],
    entityType: 0,
    id: 0,
    name: '',
  });
  const { setLogin } = useContext(Context);
  const getList = useCallback(
    async (search?: URLSearchParams, pushing = false): Promise<void> => {
      try {
        setInitLoading(true);
        const responseEntities = await api[entityKey].list<ServerResponse<T>>(
          search,
        );

        if (responseEntities) {
          if (!responseEntities.data) {
            // @ts-ignore
            setEntityList(responseEntities);
          } else {
            setEntityList(responseEntities.data);
          }
          // setEntityList((prev) => [...prev, ...responseEntities.data]);
        }
        return Promise.resolve();
      } catch (error) {
        if (error.response) {
          if (error.response.status === 409) {
            enqueueSnackbar(error.response.data.error.message, {
              variant: 'error',
            });
          } else if (error.response.status === 404) {
            enqueueSnackbar(error.response.data.error.message, {
              variant: 'error',
            });
          } else if (error.response.status === 401) {
            const userJSON = localStorage.getItem('profile');
            let user: AuthResponse = {
              data: {
                token: '',
                browser: '',
                user: {
                  surname: '',
                  email: '',
                  name: '',
                  roleName: '',
                  subscription: false,
                  createdAt: '',
                  id: 0,
                  status: 1,
                  role: 1,
                  subdivisions: [],
                },
              },
            };
            if (userJSON) {
              user = JSON.parse(userJSON);
              localStorage.removeItem('profile');
              if (setLogin) {
                setLogin(false);
              }
              // localStorage.removeItem('login');
            }
          } else {
            enqueueSnackbar('Ошибка сервера', {
              variant: 'error',
            });
          }
        }

        return Promise.reject(error);
      } finally {
        setInitLoading(false);
      }
    },
    [],
  );
  const getEntityList = useCallback(
    (search?: URLSearchParams): Promise<void> => {
      return getList(search);
    },
    [getList],
  );
  const setHistory = useCallback(async (array: T[]) => {
    setEntityList(array);
  }, []);
  const getlogList = useCallback(
    async (search?: URLSearchParams, pushing = false): Promise<void> => {
      try {
        setInitLoading(true);
        const responseEntities = await api[entityKey].list<ServerResponse<T>>(
          search,
        );

        if (responseEntities) {
          setEntityList((prev) => {
            return [...prev, ...responseEntities.data];
          });
        }
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 409) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 404) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 401) {
          const userJSON = localStorage.getItem('profile');
          let user: AuthResponse = {
            data: {
              token: '',
              browser: '',
              user: {
                surname: '',
                email: '',
                name: '',
                roleName: '',
                subscription: false,
                createdAt: '',
                id: 0,
                status: 1,
                role: 1,
                subdivisions: [],
              },
            },
          };
          if (userJSON) {
            user = JSON.parse(userJSON);
            localStorage.removeItem('profile');
            if (setLogin) {
              setLogin(false);
            }
            // localStorage.removeItem('login');
          }
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setInitLoading(false);
      }
    },
    [],
  );

  const getLogsList = useCallback(
    (search?: URLSearchParams): Promise<void> => {
      return getlogList(search);
    },
    [getlogList],
  );
  const clearEntityList = () => {
    setEntityList([]);
  };
  const getEntity = useCallback(
    async (id: number, search?: URLSearchParams): Promise<void> => {
      try {
        setInitLoading(true);
        const responseEntity = await api[entityKey].get<EntityResponse<T>>(
          id,
          search,
        );
        if (!responseEntity.data) {
          // @ts-ignore
          setEntity(responseEntity);
        } else {
          setEntity(responseEntity.data);
        }
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 409) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 404) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 401) {
          const userJSON = localStorage.getItem('profile');
          let user: AuthResponse = {
            data: {
              token: '',
              browser: '',
              user: {
                surname: '',
                email: '',
                name: '',
                roleName: '',
                subscription: false,
                createdAt: '',
                id: 0,
                status: 1,
                role: 1,
                subdivisions: [],
              },
            },
          };
          if (userJSON) {
            user = JSON.parse(userJSON);
            localStorage.removeItem('profile');
            if (setLogin) {
              setLogin(false);
            }
            // localStorage.removeItem('login');
          }
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setInitLoading(false);
      }
    },
    [],
  );

  const getTree = useCallback(async (id: number, page?: number, limit?: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].getTree<EntityResponse<T>>(
        id,
        page,
        limit,
      );
      
      const newData = responseEntity.data as any;
      
      // Если это не первая страница, объединяем данные с существующими
      if (page && page > 1) {
        setEntity((currentEntity) => {
          if (!currentEntity) {
            return responseEntity.data;
          }
          
          const currentData = currentEntity as any;
          
          // Объединяем subdivisions, фильтруя дубликаты по id
          if (currentData.subdivisions && newData.subdivisions) {
            const existingIds = new Set(currentData.subdivisions.map((sub: any) => sub.id));
            const newSubdivisions = newData.subdivisions.filter((sub: any) => !existingIds.has(sub.id));
            currentData.subdivisions = [...currentData.subdivisions, ...newSubdivisions];
          }
          // Объединяем staff, фильтруя дубликаты по id
          if (currentData.staff && newData.staff) {
            const existingStaffIds = new Set(currentData.staff.map((s: any) => s.id));
            const newStaff = newData.staff.filter((s: any) => !existingStaffIds.has(s.id));
            currentData.staff = [...currentData.staff, ...newStaff];
          }
          
          // Сохраняем totalPage из новой страницы
          if (newData.totalPage !== undefined) {
            currentData.totalPage = newData.totalPage;
          }
          
          return currentData;
        });
      } else {
        // Сохраняем totalPage при первой загрузке
        setEntity(responseEntity.data);
      }
      
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, [entityKey]);

  const getTreeWithIndeterminate = useCallback(async (id: number): Promise<
    void
  > => {
    try {
      setLoading(true);
      await api[entityKey]
        .getTreeWithIndeterminate<AccessTree>(id)
        .then((resp) => setAccessTree(resp));
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getSearchOrgTree = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].searchTreeOrg<
        EntityResponse<T>
      >(id);
      setEntity(responseEntity.data);
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getSearchSubTree = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].searchTreeSub<
        EntityResponse<T>
      >(id);
      setEntity(responseEntity.data);
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getSearchStaffTree = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].searchTreeStaff<
        EntityResponse<T>
      >(id);
      setEntity(responseEntity.data);
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const search = useCallback(async (str: URLSearchParams): Promise<void> => {
    try {
      setInitLoading(true);
      const responseEntity = await api[entityKey].search<Search>(str);
      setSearchResult((prev) => {
        return {
          data: {
            staffs: {
              staffs:
                !prev.data.staffs.staffs.find(
                  (s) => s === responseEntity.data.staffs.staffs[0],
                ) && prev.str.get('filter[text]') === str.get('filter[text]')
                  ? [
                      ...prev.data.staffs.staffs,
                      ...responseEntity.data.staffs.staffs,
                    ]
                  : responseEntity.data.staffs.staffs,
              totalCount:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.staffs.totalCount
                  : responseEntity.data.staffs.totalCount,
              totalPages:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.staffs.totalPages
                  : responseEntity.data.staffs.totalPages,
            },

            subdivisions: {
              subdivisions:
                !prev.data.subdivisions.subdivisions.find(
                  (s) => s === responseEntity.data.subdivisions.subdivisions[0],
                ) && prev.str.get('filter[text]') === str.get('filter[text]')
                  ? [
                      ...prev.data.subdivisions.subdivisions,
                      ...responseEntity.data.subdivisions.subdivisions,
                    ]
                  : responseEntity.data.subdivisions.subdivisions,
              totalCount:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.subdivisions.totalCount
                  : responseEntity.data.subdivisions.totalCount,
              totalPages:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.subdivisions.totalPages
                  : responseEntity.data.subdivisions.totalPages,
            },
            organizations: {
              organizations:
                !prev.data.organizations.organizations.find(
                  (s) =>
                    s === responseEntity.data.organizations.organizations[0],
                ) && prev.str.get('filter[text]') === str.get('filter[text]')
                  ? [
                      ...prev.data.organizations.organizations,
                      ...responseEntity.data.organizations.organizations,
                    ]
                  : responseEntity.data.organizations.organizations,
              totalPages:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.organizations.totalPages
                  : responseEntity.data.organizations.totalPages,
              totalCount:
                prev.str.get('filter[text]') === str.get('filter[text]')
                  ? prev.data.organizations.totalCount
                  : responseEntity.data.organizations.totalCount,
            },
          },
          str,
        };
      });

      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setInitLoading(false);
    }
  }, []);

  const clearSearchResult = useCallback(() => {
    setSearchResult({
      data: {
        organizations: { organizations: [], totalCount: 1, totalPages: 1 },
        staffs: { staffs: [], totalCount: 1, totalPages: 1 },
        subdivisions: { subdivisions: [], totalCount: 1, totalPages: 1 },
      },
      str: new URLSearchParams(''),
    });
  }, []);

  const importer = useCallback(async (id?: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].import<EntityResponse<T>>();
      // setEntity(responseEntity.data);
      enqueueSnackbar('Начат импорт', {
        variant: 'success',
      });
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeEntity = useCallback(
    async (id: number, type?: number, value?: boolean): Promise<void> => {
      try {
        setLoading(true);
        const responseEntity = await api[entityKey].patch<T>(id, type, value);
        setEntity(responseEntity);
        enqueueSnackbar(messages?.changeEntity ?? 'Сущность успешно изменена', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 409) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 404) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 401) {
          const userJSON = localStorage.getItem('profile');
          let user: AuthResponse = {
            data: {
              token: '',
              browser: '',
              user: {
                surname: '',
                email: '',
                name: '',
                roleName: '',
                subscription: false,
                createdAt: '',
                id: 0,
                status: 1,
                role: 1,
                subdivisions: [],
              },
            },
          };
          if (userJSON) {
            user = JSON.parse(userJSON);
            localStorage.removeItem('profile');
            if (setLogin) {
              setLogin(false);
            }
            // localStorage.removeItem('login');
          }
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteEntity = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading(true);
      await api[entityKey].delete<T>(id);
      setEntity(null);
      enqueueSnackbar(messages?.deleteEntity ?? 'Сущность успешно удалена', {
        variant: 'success',
      });
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeAccess = useCallback(
    async (id: number, newAccess: INewAccess): Promise<void> => {
      try {
        setLoading(true);
        const responseEntity = await api[entityKey].changeAccessEmployee<T>(
          id,
          newAccess,
        );
        enqueueSnackbar('Доступ изменен.', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateEntity = useCallback(async (entityUpdate: T): Promise<T> => {
    try {
      setLoading(true);
      const updatedEntity = await api[entityKey].update<T>(entityUpdate);
      enqueueSnackbar(messages?.updateEntity ?? 'Сущность успешно обновлена', {
        variant: 'success',
      });
      return Promise.resolve(updatedEntity);
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fullUpdateEntity = useCallback(async (entityUpdate: any): Promise<
    any
  > => {
    try {
      setLoading(true);
      const updatedEntity = await api[entityKey].fullUpdate<T>(entityUpdate);
      enqueueSnackbar('Сущность успешно обновлена', {
        variant: 'success',
      });
      return Promise.resolve(updatedEntity);
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const blockUser = useCallback(
    async (id: number, method?: 'Delete' | 'Block'): Promise<void> => {
      try {
        setLoading(true);
        const responseEntity = await (method === 'Delete'
          ? api[entityKey].delete<T>(id)
          : api[entityKey].block<T>(id));
        setEntity(responseEntity);
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 404) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 401) {
          const userJSON = localStorage.getItem('profile');
          let user: AuthResponse = {
            data: {
              token: '',
              browser: '',
              user: {
                surname: '',
                email: '',
                name: '',
                roleName: '',
                subscription: false,
                createdAt: '',
                id: 0,
                status: 1,
                role: 1,
                subdivisions: [],
              },
            },
          };
          if (userJSON) {
            user = JSON.parse(userJSON);
            localStorage.removeItem('profile');
            if (setLogin) {
              setLogin(false);
            }
            // localStorage.removeItem('login');
          }
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const unblockUser = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].unblock<T>(id);
      setEntity(responseEntity);
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createEntity = useCallback(async (entityCreate: K): Promise<T> => {
    try {
      setLoading(true);
      const createdEntity = await api[entityKey].entry<T, K>(entityCreate);
      enqueueSnackbar(messages?.createEntity ?? 'Сущность успешно создана', {
        variant: 'success',
      });
      return Promise.resolve(createdEntity);
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 401) {
        const userJSON = localStorage.getItem('profile');
        let user: AuthResponse = {
          data: {
            token: '',
            browser: '',
            user: {
              surname: '',
              email: '',
              name: '',
              roleName: '',
              subscription: false,
              createdAt: '',
              id: 0,
              status: 1,
              role: 1,
              subdivisions: [],
            },
          },
        };
        if (userJSON) {
          user = JSON.parse(userJSON);
          localStorage.removeItem('profile');
          if (setLogin) {
            setLogin(false);
          }
          // localStorage.removeItem('login');
        }
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (entityCreate: K): Promise<T> => {
    try {
      setLoading(true);
      const createdEntity = await api[entityKey].entry<T, K>(entityCreate);
      return Promise.resolve(createdEntity);
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'warning',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      throw error;
      // return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const auth = useCallback(async (entityCreate: K): Promise<T> => {
    try {
      setLoading(true);
      const createdEntity = await api[entityKey].auth<T, K>(entityCreate);
      enqueueSnackbar('Вход успешно выполнен', {
        variant: 'success',
      });

      localStorage.setItem('profile', JSON.stringify({ ...createdEntity }));
      return Promise.resolve(createdEntity);
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'warning',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }

      // enqueueSnackbar('Неверный логин, или пароль', {
      //   variant: 'warning',
      // });
      throw error;
      // return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const logOut = useCallback(async (): Promise<T> => {
    try {
      setLoading(true);
      const userJSON = localStorage.getItem('profile');
      let user: AuthResponse = {
        data: {
          token: '',
          browser: '',
          user: {
            surname: '',
            email: '',
            name: '',
            roleName: '',
            subscription: false,
            createdAt: '',
            id: 0,
            status: 1,
            role: 1,
            subdivisions: [],
          },
        },
      };
      if (userJSON) {
        user = JSON.parse(userJSON);
        localStorage.removeItem('profile');
        // localStorage.removeItem('login');
      }

      const createdEntity = await api[entityKey].logout<T, K>(user.data.token);

      // enqueueSnackbar('Сущность успешно создана', {
      //   variant: 'success',
      // });
      return Promise.resolve(createdEntity);
    } catch (error) {
      enqueueSnackbar('Ошибка сервера', {
        variant: 'error',
      });
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeStatusStaff = useCallback(
    async (id: number, status: number): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changeStatusStaff<T>(id);
        if (status === 1) {
          enqueueSnackbar('Пользователь скрыт.', {
            variant: 'success',
          });
        }
        if (status === 2) {
          enqueueSnackbar('Пользователь доступен для поиска и оценки.', {
            variant: 'success',
          });
        }

        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 409) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else if (error.response.status === 404) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const sendEmail = useCallback(async (email: string): Promise<void> => {
    try {
      setLoading(true);
      await api[entityKey].sendEmail<T>(email);
      enqueueSnackbar('Письмо успешно отправлено', {
        variant: 'success',
      });
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 409) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else if (error.response.status === 404) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAccessEmployee = useCallback(async (emloyeeId: number): Promise<
    void
  > => {
    try {
      setLoading(true);
      const responseEntity = await api[entityKey].getEmployeeAccess<
        IAccessList
      >(emloyeeId);
      setAccessList(responseEntity);
      return Promise.resolve();
    } catch (error) {
      enqueueSnackbar('Ошибка сервера', {
        variant: 'error',
      });
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAllSubdivisions = useCallback(async (): Promise<Subdivision[]> => {
    try {
      setLoading(true);
      const response: Subdivision[] = await api[entityKey].getAllSubdivisions<
        Subdivision[]
      >();
      return Promise.resolve(response);
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeParent = useCallback(
    async (
      newParentId: number,
      currentId: number,
      isStuff: boolean,
    ): Promise<void> => {
      try {
        setLoading(true);
        if (!isStuff) {
          await api[entityKey].changeParentDivision<T>(newParentId, currentId);
        } else {
          await api[entityKey].changeParentStaff<T>(newParentId, currentId);
        }

        enqueueSnackbar('Родительское подразделение изменено.', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const restorePassword = useCallback(async (entity: T): Promise<void> => {
    try {
      setLoading(true);
      await api[entityKey].restorePassword<T>(entity);
      enqueueSnackbar('Пароль успешно изменён', {
        variant: 'success',
      });
      return Promise.resolve();
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changePasswordAdmin = useCallback(
    async (newPassword: string, id: number): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changePasswordAdmin<T>(newPassword, id);
        enqueueSnackbar('Пароль успешно изменён', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const changePasswordUser = useCallback(
    async (oldPassword: string, newPassword: string): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changePasswordUser<T>(oldPassword, newPassword);
        enqueueSnackbar('Пароль успешно изменён', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getConfigurationStuff = useCallback(async (): Promise<
    IStaffSettings
  > => {
    try {
      setLoading(true);
      const response: IStuffResponse = await api[
        entityKey
      ].getConfigurationStuff<IStuffResponse>();
      return Promise.resolve(response.staffSettings);
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfigurationSubdivision = useCallback(async (): Promise<
    ISubdivisionSettings
  > => {
    try {
      setLoading(true);
      const response: ISubdivisionResponse = await api[
        entityKey
      ].getConfigurationSubdivision<ISubdivisionResponse>();
      return Promise.resolve(response.subdivisionSettings);
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getConfigurationOrganization = useCallback(async (): Promise<
    IOrganizationSettings
  > => {
    try {
      setLoading(true);
      const response: IOrganizationResponse = await api[
        entityKey
      ].getConfigurationOrganization<IOrganizationResponse>();
      return Promise.resolve(response.organizationSettings);
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeConfigurationStuff = useCallback(
    async (staffSettings: IStaffSettings): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changeConfigurationStuff<T>(staffSettings);
        enqueueSnackbar('Конфигурации для сотрудников изменены.', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const changeConfigurationSubdivision = useCallback(
    async (subdivisionSettings: ISubdivisionSettings): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changeConfigurationSubdivision<T>(
          subdivisionSettings,
        );
        enqueueSnackbar('Конфигурации для подразделений изменены.', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const changeConfigurationOrganization = useCallback(
    async (organizationSettings: IOrganizationSettings): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changeConfigurationOrganization<T>(
          organizationSettings,
        );
        enqueueSnackbar('Конфигурации для организаций изменены.', {
          variant: 'success',
        });
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const changeAnalyticsAccess = useCallback(
    async (id: number, newAccess: boolean): Promise<void> => {
      try {
        setLoading(true);
        await api[entityKey].changeAnalyticsAccess<T>(id, newAccess);
        if (newAccess) {
          enqueueSnackbar('Доступ в аналитику разрешен.', {
            variant: 'success',
          });
        } else {
          enqueueSnackbar('Доступ в аналитику запрещен.', {
            variant: 'success',
          });
        }
        return Promise.resolve();
      } catch (error) {
        if (error.response.status === 400) {
          enqueueSnackbar(error.response.data.error.message, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar('Ошибка сервера', {
            variant: 'error',
          });
        }
        return Promise.reject(error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getLoginData = useCallback(async (id: number): Promise<ILoginData> => {
    try {
      setLoading(true);
      const response: ResponseLoginData = await api[entityKey].getLoginData<
        ResponseLoginData
      >(id);
      return Promise.resolve(response.data);
    } catch (error) {
      if (error.response.status === 400) {
        enqueueSnackbar(error.response.data.error.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Ошибка сервера', {
          variant: 'error',
        });
      }
      return Promise.reject(error);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getEntityList,
    getLogsList,
    getEntity,
    entity,
    logOut,
    entityList,
    initLoading,
    loading,
    createEntity,
    login,
    clearEntityList,
    changeEntity,
    blockUser,
    unblockUser,
    updateEntity,
    importer,
    fullUpdateEntity,
    search,
    searchResult,
    getTree,
    getTreeWithIndeterminate,
    getSearchOrgTree,
    getSearchSubTree,
    getSearchStaffTree,
    clearSearchResult,
    sendEmail,
    restorePassword,
    auth,
    setHistory,
    changeParent,
    getAccessEmployee,
    accessList,
    accessTree,
    changeAccess,
    changeStatusStaff,
    changePasswordAdmin,
    changePasswordUser,
    getAllSubdivisions,
    getConfigurationStuff,
    getConfigurationSubdivision,
    getConfigurationOrganization,
    changeConfigurationStuff,
    changeConfigurationSubdivision,
    changeConfigurationOrganization,
    changeAnalyticsAccess,
    getLoginData,
    deleteEntity,
  };
};
